import React, { useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate, useParams } from 'react-router';
import { SingleColumnPageBase } from 'src/components/PageBase/SingleColumnPageBase';
import { useGetProjectQuery, useGetProjectSettingsQuery } from 'src/api/v2/project-v2-ApiSlice';
import { ElectricalPropertiesSection } from 'src/pages/ProjectSettingsPage/sections/ElectricalPropertiesSection';
import { MiscSettingsSection } from 'src/pages/ProjectSettingsPage/sections/MiscSettingsSection';
import { QuotePropertiesSection } from 'src/pages/ProjectSettingsPage/sections/QuotePropertiesSection';
import { WizardDevicesSection } from 'src/pages/ProjectSettingsPage/sections/WizardDevicesSection';
import { hasPermissionForFuncInRole } from 'src/utils/controllerHasPermissionsAdapter';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';
import { DOUBLE_SPACING } from 'src/theme/style-consts';

const PAGE_CONTENT_MAX_WIDTH = 1100;
const GENERAL_INFO_EDITOR_ROLES = ['project_owner', 'project_admin'];

export const ProjectSettingsPage = () => {
    const params = useParams();
    const projectId = Number(params.projectId);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const { data: extendedProject } = useGetProjectQuery(projectId);
    const { data: settings, isError: isSettingsError } = useGetProjectSettingsQuery(projectId);

    useEffect(() => {
        if (isSettingsError) {
            dispatch(showSnackBar({
                text: ts('project_settings_load_error', 'Failed to load project settings'),
                severity: 'error',
            }));
        }
    }, [isSettingsError, dispatch]);

    const handleBack = () => {
        navigate(location.state?.from ?? '/projects');
    };

    const canEdit = (functionName: string) => extendedProject != null
        && !extendedProject.readOnly
        && hasPermissionForFuncInRole(functionName, extendedProject.userRole);
    const canEditElectricalPlan = canEdit('settings_sect.edit_electrical_plan');
    const canEditWizardProducts = canEdit('settings_sect.edit_wizard_products');
    const canEditGeneralInfo = extendedProject != null
        && !extendedProject.readOnly
        && GENERAL_INFO_EDITOR_ROLES.includes(extendedProject.userRole);

    return (
        <SingleColumnPageBase
            pageContent={
                <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
                    <Stack spacing={DOUBLE_SPACING} sx={{ maxWidth: PAGE_CONTENT_MAX_WIDTH, marginX: 'auto' }}>
                        <Box>
                            <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
                                {ts('q_sec_gotoback', 'Go back')}
                            </Button>
                        </Box>
                        <Box>
                            <Typography variant="h5">
                                {ts('project_list_menu_settings', 'Project Settings')}
                            </Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>
                                {extendedProject?.project.project_name}
                            </Typography>
                        </Box>
                        {settings && extendedProject && (
                            <>
                                <ElectricalPropertiesSection
                                    projectId={projectId}
                                    settings={settings}
                                    canEdit={canEditElectricalPlan}
                                />
                                <WizardDevicesSection
                                    projectId={projectId}
                                    settings={settings}
                                    canEdit={canEditWizardProducts}
                                />
                                <QuotePropertiesSection
                                    projectId={projectId}
                                    generalInfo={extendedProject.project.general_info}
                                    canEdit={canEditGeneralInfo}
                                />
                                <MiscSettingsSection
                                    projectId={projectId}
                                    project={extendedProject.project}
                                    settings={settings}
                                    canEdit={!extendedProject.readOnly}
                                />
                            </>
                        )}
                    </Stack>
                </Box>
            }
        />
    );
};
